import {useEffect, useRef} from "react";
import {analyticsService} from "./analytics.service.js";
import {useModeration} from "./authentication/authenticationQueries.js";

/**
 * Fires the `planning_viewed` analytics event (#515) once the planning page is
 * mounted for a given company. Re-fires if the company changes (account switch).
 *
 * No-op while `companyId` is undefined (context still loading) and — like every
 * other analytics surface (see analyticsSession) — while a moderation/support
 * session is impersonating a customer: support traffic must never pollute the
 * adoption metrics. The guard is kept inside the hook so no call site can forget
 * it.
 *
 * `analyticsService.track` is itself a no-op outside prod/dev (capture gate).
 */
export function useTrackPlanningViewed(companyId: string | undefined): void {
  const {isModeration, isModerationLoading} = useModeration();
  const trackedCompanyIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (
      !companyId ||
      isModerationLoading ||
      isModeration ||
      trackedCompanyIdRef.current === companyId
    ) {
      return;
    }
    trackedCompanyIdRef.current = companyId;
    analyticsService.track("planning_viewed", {companyId});
  }, [companyId, isModeration, isModerationLoading]);
}
